// The dashboard's chrome — everything an rdrive dashboard renders around its pages. Dumb by
// rule: props in, emits out, slots for the app's content; no store, no SQL, no analytics
// (PLATFORM.md §3.2 rules 1, 9). Built from project-overview's App/component.vue (6b-1), whose
// DOM it reproduces: v-app → app bar (nav + [app-bar-append slot] + kebab) → missing-params
// dialog → DuckDB query dialog → v-main → .content-container → [default slot] + WelcomeDialog +
// [fab slot].
//
//   props:  navItems       (Array)    [{ name, icon, text }] — one app-bar button per item,
//                                     `:to="{ name }"`, active when the current route's name
//                                     matches; `text` is shown from `sm` up (translated by the app)
//           missingParams  (Array)    required URL params that are missing (usePlatformParams'
//                                     `missing`); non-empty ⇒ the persistent warning dialog
//           exporting      (Boolean)  the DuckDB dialog's export spinner
//           queryRunner    (Object)   OPT-IN (AM_ABSORPTION_PLAN F1), default null: passed as is
//                                     to the DuckDB dialog's `query` — its SQL runner,
//                                     { run(sql) → Promise<rows | { columns?, rows }>,
//                                     shortcuts?: [{ label, sql }], initialSql? } (the app owns
//                                     the connection; asset-management: conn.queryRaw + its model
//                                     sanity queries). null ⇒ the dialog exactly as before
//           showWelcome    (Boolean)  v-model:showWelcome — the welcome dialog's open state
//           welcome        (Object)   WelcomeDialog's other props, bound with v-bind:
//                                     { projectName, initializing, dataLoaded, apiProgress,
//                                     totalApiRequests, queryString, heroImage?,
//                                     showMarketingPanel?, contained? }
//   emits:  update:showWelcome        the welcome dialog opened/closed itself
//           load                      WelcomeDialog's Load button (the app runs the data load)
//           copyQueryString           WelcomeDialog's dev-mode title double-click
//           marketingItemOpen         WelcomeDialog's marketing panel (opt-in), payload as is
//           marketingItemVisit        ditto
//           export                    the DuckDB dialog's "Export to Zip" (the app exports)
//           toggleTheme               the kebab's theme switch (the app owns the theme)
//           closeMissingParams        the missing-params dialog's Close (it has closed itself)
//   slots:  default                   the page area — the app's <router-view>
//           fab                       after the welcome dialog, inside .content-container —
//                                     the app's floating action button(s)
//           app-bar-append            OPT-IN (AM_ABSORPTION_PLAN B2): in the app bar, right after
//                                     the nav buttons, before the spacer and the kebab — a direct
//                                     flex child of the toolbar (asset-management: its DEMO chip
//                                     on ?demo=true). Not provided ⇒ an empty slot fragment (two
//                                     empty text anchors, no element): the bar's elements, boxes
//                                     and innerHTML are what they were without the slot
//
// Shell-owned state: the DuckDB dialog's open state (opened from the kebab), fullscreen
// (browser API + change listeners), and the missing-params dialog's dismissal.
// Strings come from the colocated ./messages folder through a LOCAL composer (rule 11).
import { WelcomeDialog } from '@rdrive/plugin-welcome-dialog';
import { DuckDBQueryDialog } from '@rdrive/plugin-duckdb-query-dialog';
import setup from './setup.js';

export default {
  name: 'dashboard-shell',
  components: { WelcomeDialog, DuckDBQueryDialog },
  props: {
    navItems: {
      type: Array,
      default: () => [],
      validator: (items) => items.every((item) => item && typeof item.name === 'string'),
    },
    missingParams: {
      type: Array,
      default: () => [],
    },
    exporting: {
      type: Boolean,
      default: false,
    },
    queryRunner: {
      type: Object,
      default: null,
    },
    showWelcome: {
      type: Boolean,
      default: false,
    },
    welcome: {
      type: Object,
      required: true,
    },
  },
  emits: [
    'update:showWelcome',
    'load',
    'copyQueryString',
    'marketingItemOpen',
    'marketingItemVisit',
    'export',
    'toggleTheme',
    'closeMissingParams',
  ],
  setup,
};
